// src/components/resumo/quiz/QuizAdminModal.tsx
// 🎯 Modal Administrativo de Gerenciamento de Quiz
// ✅ IDENTIDADE GRAIA APLICADA
// ✅ CORRIGIDO: Tipos compatíveis com quiz.types.ts

import React, { useState, useEffect } from 'react';
import { X, ChevronLeft, ChevronRight, Save, Trash2, HelpCircle } from 'lucide-react';
import { QuizBadgeData, QuizQuestion, QuizAlternative } from './quiz.types';
import './QuizAdminModal.css';

interface QuizAdminModalProps {
    quizData: QuizBadgeData | null;
    onSave: (data: QuizBadgeData) => void;
    onClose: () => void;
    onDeleteQuiz: () => void;
    darkMode: boolean;
}

const QuizAdminModal: React.FC<QuizAdminModalProps> = ({ quizData, onSave, onClose, onDeleteQuiz, darkMode }) => {
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [questions, setQuestions] = useState<QuizQuestion[]>(
        quizData?.questions || [createEmptyQuestion(0)]
    );

    useEffect(() => {
        if (!questions || questions.length === 0) {
            setQuestions([createEmptyQuestion(0)]);
        }
    }, [questions]);

    function createEmptyQuestion(order: number): QuizQuestion {
        return {
            id: `q_${Date.now()}_${Math.random()}`,
            question: '',
            alternatives: {
                a: '',
                b: '',
                c: '',
                d: ''
            },
            correctAnswer: 'a',
            order
        };
    }

    if (!questions || questions.length === 0 || !questions[currentQuestionIndex]) {
        return null; // Ou loading spinner
    }

    const currentQuestion = questions[currentQuestionIndex];
    const totalQuestions = questions.length;

    if (!currentQuestion) {
        return (
            <div className="quiz-admin-modal-overlay" onClick={onClose}>
                <div className={`quiz-admin-modal ${darkMode ? 'dark' : ''}`} onClick={(e) => e.stopPropagation()}>
                    <div className="quiz-admin-header">
                        <div className="quiz-admin-title">
                            <HelpCircle size={20} className="quiz-admin-icon" />
                            <h2>Erro ao carregar quiz</h2>
                        </div>
                        <button className="quiz-admin-close" onClick={onClose}>
                            <X size={20} />
                        </button>
                    </div>
                    <div style={{ padding: '40px', textAlign: 'center' }}>
                        <p>Não foi possível carregar a questão. Tente novamente.</p>
                        <button className="quiz-admin-btn quiz-admin-btn-primary" onClick={onClose}>
                            Fechar
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    const handleQuestionChange = (value: string) => {
        const updated = [...questions];
        updated[currentQuestionIndex].question = value;
        setQuestions(updated);
    };

    const handleAlternativeChange = (key: QuizAlternative, value: string) => {
        const updated = [...questions];
        updated[currentQuestionIndex].alternatives[key] = value;
        setQuestions(updated);
    };

    const handleCorrectAnswerChange = (key: QuizAlternative) => {
        const updated = [...questions];
        updated[currentQuestionIndex].correctAnswer = key;
        setQuestions(updated);
    };

    const handleExplanationChange = (value: string) => {
        const updated = [...questions];
        updated[currentQuestionIndex].explanation = value;
        setQuestions(updated);
    };

    const handleAddQuestion = () => {
        setQuestions([...questions, createEmptyQuestion(questions.length)]);
        setCurrentQuestionIndex(questions.length);
    };

    const handleDeleteQuestion = () => {
        if (questions.length === 1) {
            alert('Deve haver pelo menos uma questão.');
            return;
        }

        const updated = questions.filter((_, i) => i !== currentQuestionIndex);

        // Reordenar
        updated.forEach((q, idx) => {
            q.order = idx;
        });

        setQuestions(updated);

        if (currentQuestionIndex >= updated.length) {
            setCurrentQuestionIndex(updated.length - 1);
        }
    };

    const handleNext = () => {
        if (currentQuestionIndex < totalQuestions - 1) {
            setCurrentQuestionIndex(prev => prev + 1);
        }
    };

    const handlePrevious = () => {
        if (currentQuestionIndex > 0) {
            setCurrentQuestionIndex(prev => prev - 1);
        }
    };

    const handleSave = () => {
        // Validar
        for (const q of questions) {
            if (!q.question.trim()) {
                alert('Todas as questões devem ter uma pergunta.');
                return;
            }

            const alternatives = Object.values(q.alternatives);
            if (alternatives.some(alt => !alt.trim())) {
                alert('Todas as alternativas devem ser preenchidas.');
                return;
            }

            if (!q.correctAnswer) {
                alert('Todas as questões devem ter uma resposta correta marcada.');
                return;
            }
        }

        const now = new Date().toISOString();

        const data: QuizBadgeData = {
            id: quizData?.id || `quiz_${Date.now()}`,
            questions,
            createdAt: quizData?.createdAt || now,
            updatedAt: now
        };

        onSave(data);
    };

    const getAlternativeLetter = (key: QuizAlternative): string => {
        return key.toUpperCase();
    };

    const alternativeKeys: QuizAlternative[] = ['a', 'b', 'c', 'd'];

    return (
        <div className="quiz-admin-modal-overlay" onClick={onClose}>
            <div
                className={`quiz-admin-modal ${darkMode ? 'dark' : ''}`}
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="quiz-admin-header">
                    <div className="quiz-admin-title">
                        <HelpCircle size={20} className="quiz-admin-icon" />
                        <h2>Gerenciar Quiz</h2>
                    </div>
                    <button
                        className="quiz-admin-close"
                        onClick={onClose}
                        title="Fechar"
                    >
                        <X size={20} />
                    </button>
                </div>

                {/* Navigation */}
                <div className="quiz-admin-nav">
                    <button
                        className="quiz-admin-nav-btn"
                        onClick={handlePrevious}
                        disabled={currentQuestionIndex === 0}
                        title="Questão anterior"
                    >
                        <ChevronLeft size={18} />
                    </button>

                    <div className="quiz-admin-progress">
                        <span className="quiz-admin-counter">
                            Questão {currentQuestionIndex + 1} de {totalQuestions}
                        </span>
                    </div>

                    <button
                        className="quiz-admin-nav-btn"
                        onClick={handleNext}
                        disabled={currentQuestionIndex === totalQuestions - 1}
                        title="Próxima questão"
                    >
                        <ChevronRight size={18} />
                    </button>
                </div>

                {/* Content */}
                <div className="quiz-admin-content">
                    {/* Pergunta */}
                    <div className="quiz-admin-field">
                        <label>Pergunta:</label>
                        <textarea
                            value={currentQuestion.question}
                            onChange={(e) => handleQuestionChange(e.target.value)}
                            rows={3}
                            placeholder="Digite a pergunta aqui..."
                        />
                    </div>

                    {/* Alternativas */}
                    <div className="quiz-admin-alternatives">
                        <label>Alternativas:</label>
                        {alternativeKeys.map((key) => (
                            <div key={key} className="quiz-admin-alternative">
                                <div className="quiz-admin-alternative-header">
                                    <span className="quiz-admin-alternative-letter">
                                        {getAlternativeLetter(key)}
                                    </span>
                                    <input
                                        type="radio"
                                        name="correctAnswer"
                                        checked={currentQuestion.correctAnswer === key}
                                        onChange={() => handleCorrectAnswerChange(key)}
                                        title="Marcar como resposta correta"
                                    />
                                </div>
                                <input
                                    type="text"
                                    value={currentQuestion.alternatives[key]}
                                    onChange={(e) => handleAlternativeChange(key, e.target.value)}
                                    placeholder={`Alternativa ${getAlternativeLetter(key)}`}
                                />
                            </div>
                        ))}
                    </div>

                    <div className="quiz-admin-hint">
                        Marque a alternativa correta usando o botão de rádio ao lado.
                    </div>

                    {/* Explicação */}
                    <div className="quiz-admin-field">
                        <label>Explicação (opcional):</label>
                        <textarea
                            value={currentQuestion.explanation || ''}
                            onChange={(e) => handleExplanationChange(e.target.value)}
                            rows={3}
                            placeholder="Explique a resposta correta. Aparece para o aluno depois que ele responder..."
                        />
                    </div>
                </div>

                {/* Actions */}
                <div className="quiz-admin-actions">
                    <div className="quiz-admin-actions-left">
                        <button
                            className="quiz-admin-btn quiz-admin-btn-secondary"
                            onClick={handleAddQuestion}
                        >
                            Nova Questão
                        </button>
                        <button
                            className="quiz-admin-btn quiz-admin-btn-danger"
                            onClick={handleDeleteQuestion}
                            disabled={questions.length === 1}
                            title="Excluir esta questão"
                        >
                            <Trash2 size={16} />
                            Excluir questão
                        </button>
                        <button
                            className="quiz-admin-btn quiz-admin-btn-danger"
                            onClick={() => {
                                if (window.confirm('Excluir este quiz da página? Essa ação não pode ser desfeita.')) {
                                    onDeleteQuiz();
                                }
                            }}
                            title="Excluir o quiz inteiro desta página"
                        >
                            <Trash2 size={16} />
                            Excluir quiz
                        </button>
                    </div>

                    <div className="quiz-admin-actions-right">
                        <button
                            className="quiz-admin-btn quiz-admin-btn-cancel"
                            onClick={onClose}
                        >
                            Cancelar
                        </button>
                        <button
                            className="quiz-admin-btn quiz-admin-btn-primary"
                            onClick={handleSave}
                        >
                            <Save size={16} />
                            Salvar Quiz
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default QuizAdminModal;